<template>
  <div>
    <div ref="map" class="map"></div>
    <modal
      :id="`leaflet-popup-${uniqueId}`"
      ref="popup"
      :title="popupTitle"
      size="80%"
      dialogClass="modal-xl"
      @modal-closed="onModalClose"
    >
      <layout-builder v-if="hasUrl" class="layout-builder-sub" :url="popup.url" :params="urlParams" :key="reRender" />
      <list-container v-else v-bind="listProps" />
    </modal>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet.pm'
import 'leaflet.markercluster'
import 'leaflet.featuregroup.subgroup'

import Modal from '../modal/Modal'
import pmLocalization from './pmLocalization'
import ListContainer from '../list-new/ListContainer'
import LayoutBuilder from '../layout-manager/layout-builder/LayoutBuilder.vue'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch'
import { forOwn, get, isEmpty, isString, pickBy, cloneDeep } from 'lodash'
import { mapActions } from 'vuex'

delete L.Icon.Default.prototype._getIconUrl

/**
 * @param Leaflet component
 * @description map component
 */
export default {
  name: 'Leaflet',
  components: {
    LayoutBuilder,
    ListContainer,
    Modal,
  },
  props: {
    computedReadOnly: {
      type: Boolean,
      default: false,
    },
    mapProps: {
      type: Object,
      default: () => ({}),
    },
    field: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      maxZoom: 19,
      markerCluster: new L.MarkerClusterGroup({
        disableClusteringAtZoom: 17,
      }),
      defaultMapOptions: {
        scrollWheelZoom: get(this.options, 'enableZoomOnScroll', false),
        center: [0, 0],
        zoom: 2,
      },
      layerGroups: {},
      url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      topoUrl: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
      satelliteUrl: 'http://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
      satelliteAttribution: 'google',
      attribution: '© OpenStreetMap',
      topoAttribution: 'Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)',
      defaultPmOptions: {
        position: 'topleft',
        drawMarker: false,
        drawCircleMarker: false,
        drawPolyline: false,
        drawRectangle: false,
        drawPolygon: false,
        drawCircle: false,
        editMode: false,
        dragMode: false,
        cutPolygon: false,
        removalMode: false,
      },
      map: null,
      popup: {
        url: false,
        urlParams: null,
      },
      reRender: 0,
      popupTitle: '',
      hiddenSearchData: {},
      coreParamsCache: {},
    }
  },
  computed: {
    hasUrl() {
      return get(this.popup, 'url')
    },
    listProps() {
      return {
        mongoId: get(this.popup, 'mongoId', ''),
        hiddenSearchData: this.hiddenSearchData,
        extraOptions: {
          availableViews: {
            gridView: false,
            cardView: false,
            detailView: true,
            defaultView: 'detailView',
          },
          showFilters: false,
        },
        customLayout: `<list-view-container/>`,
      }
    },
    urlParams() {
      return this.popup.urlParams
    },
    uniqueId() {
      return this.$uid()
    },
  },
  methods: {
    ...mapActions(['showModal']),
    onModalClose() {
      this.$CORE.params = this.coreParamsCache
    },
    setToolsStatus(map) {
      const limits = this.mapProps.drawOptions.limits
      if (limits) {
        limits.forEach((e) => {
          if (e.type === 'marker') {
            const markerButton = map.pm.Toolbar.buttons.drawMarker._container.getElementsByClassName(
              'leaflet-buttons-control-button',
            )[0]
            const geoSearch = document.querySelector('.leaflet-control-geosearch > a')
            if (this.markerCluster.getLayers().length >= e.max) {
              map.pm.disableDraw('Marker')
              markerButton.classList.add('disabled')
              geoSearch.classList.add('disabled')
            } else {
              markerButton.classList.remove('disabled')
              geoSearch.classList.remove('disabled')
            }
          }
          // more types here...
        })
      }
    },
    setDefaultMarkerIcon() {
      L.Marker.prototype.options.icon = this.createDivIcon(this.mapProps)
    },
    createDivIcon(layer) {
      const style = get(layer, 'properties.style', '{}')
      return L.divIcon({
        iconAnchor: [15, 40],
        popupAnchor: [0, -50],
        className: '',
        html: this.getIcon(style),
      })
    },
    parseStyle(string) {
      try {
        return JSON.parse(string)
      } catch (e) {
        console.error('Icon style is not a valid JSON.')
        return {}
      }
    },
    getIcon(styleJSON) {
      const style = this.parseStyle(styleJSON)
      const color = get(style, 'color', '#00aeff')
      const backgroundColor = get(style, 'background-color', '#fff')
      const materialIcon = get(style, 'icon', 'map')
      return `<span
                style="background-color: ${backgroundColor};
                border: 2px solid ${color};
                border-radius: 50% 50% 50% 0;
                padding: 2px;
                transform: rotate(-45deg);
                width: 32px;
                height: 32px;
                display: block;"
              >
                <span
                  class="material-icons md-18"
                  style="
                  background-color: ${backgroundColor};
                  color: ${color};
                  transform: rotate(45deg);
                  border-radius: 50%;
                  padding: 3px;"
                >
                  ${materialIcon}
                </span>
              </span>`
    },
    checkDrawAndEdit() {
      let actualOptions = Object.assign({}, this.defaultPmOptions)
      if (this.computedReadOnly) {
        this.map.pm.addControls(actualOptions)
      } else {
        Object.assign(actualOptions, this.mapProps.drawOptions.draw)
        this.map.pm.addControls(actualOptions)
        this.setToolsStatus(this.map)
      }
    },
    setLocales() {
      this.map.pm.setLang('customLocale', pmLocalization(this))
      this.map.zoomControl._zoomInButton.title = `${this.$i18n.t('zoom_in_button')}`
      this.map.zoomControl._zoomOutButton.title = `${this.$i18n.t('zoom_out_button')}`
    },
    createLayerGroup(data) {
      return L.geoJSON(data, {
        // to style Point features
        pointToLayer: (layer, latLng) => {
          if (get(layer, 'geometry.type') === 'Point') {
            return L.marker(latLng, { icon: this.createDivIcon(layer) })
          }
        },
        // to style LineString features
        style: (layer) => {
          const style = get(layer, 'properties.style')
          return style && isString(style) ? JSON.parse(style) : '{}'
        },
      })
    },
    populateMarkerCluster(data) {
      this.transferPropertiesToFeatures(data)
      const layerGroup = this.createLayerGroup(data)
      this.bindPropertiesToLayers(layerGroup)
      this.markerCluster.addLayers(layerGroup.getLayers())
    },
    transferPropertiesToFeatures(data) {
      if (!this.mapProps.hasOwnProperty('properties')) return
      for (let feature of data.features) {
        feature.properties = Object.assign(feature.properties, this.mapProps.properties)
      }
    },
    bindPropertiesToLayers(layerGroup, key = 'layer') {
      layerGroup.eachLayer((layer) => {
        const tooltip = get(layer, 'feature.properties.tooltip', '')
        layer.bindTooltip(this.$i18n.t(tooltip.length ? tooltip : 'click_for_more_info'), key)
        layer.on('click', () => {
          const popup = get(layer, 'feature.properties.popup', '')
          this.hiddenSearchData = get(layer, 'feature.properties.hiddenSearchData', {})
          if (isEmpty(popup)) return
          this.popupTitle = this.$i18n.t(tooltip)
          if (isString(popup)) {
            this.popup.url = popup
            const urlParams = get(layer, 'feature.properties.urlParams', '')
            if (urlParams.length) {
              this.popup.urlParams = JSON.parse(urlParams)
            }
          } else {
            this.popup = popup
            this.popup.url = false
          }
          this.reRender++
          this.$nextTick(() => {
            Object.assign(this.$CORE.params, this.popup.urlParams)
            this.showModal({ id: `leaflet-popup-${this.uniqueId}` })
          })
        })
        layer.on('pm:edit', () => {
          this.$emit('map-changed', {
            payload: this.markerCluster.toGeoJSON(),
          })
        })
      })
    },
    getLocalizedLayers(defaultLayers) {
      const layers = {}
      Object.keys(defaultLayers).map((key) => {
        layers[this.$i18n.t(key)] = defaultLayers[key]
      })
      return layers
    },
    handleMapEvents(map) {
      map.on('pm:create', (e) => {
        e.layer.bindTooltip(this.$i18n.t('draw_items'))
        e.layer.on('pm:edit', () => {
          this.$emit('map-changed', {
            payload: this.markerCluster.toGeoJSON(),
          })
        })
        map.removeLayer(e.layer)
        this.markerCluster.addLayer(e.layer)
        this.setToolsStatus(map)
        this.$emit('map-changed', { payload: this.markerCluster.toGeoJSON() })
      })

      map.on('pm:remove', (e) => {
        this.markerCluster.removeLayer(e.layer)
        this.setToolsStatus(map)
        this.$emit('map-changed', { payload: this.markerCluster.toGeoJSON() })
      })

      map.on('geosearch/showlocation', (e) => {
        const layer = L.marker(e.marker._latlng)
        this.markerCluster.addLayer(layer)
        this.setToolsStatus(map)
        this.$emit('map-changed', { payload: this.markerCluster.toGeoJSON() })
      })
    },
    setMapProperties() {
      const streetView = L.tileLayer(this.url, {
        maxZoom: this.maxZoom,
        attribution: this.attribution,
      })
      const satelliteView = L.tileLayer(this.satelliteUrl, {
        maxZoom: this.maxZoom,
        attribution: this.satelliteAttribution,
      })
      const topoView = L.tileLayer(this.topoUrl, {
        maxZoom: this.maxZoom,
        topoAttribution: this.topoAttribution,
      })

      let defaultLayers = {
        streetView: streetView,
        satelliteView: satelliteView,
        topoView: topoView,
      }

      const defaultLayer = get(this.options, 'availableViews.defaultView')
      const baseLayer = defaultLayer && defaultLayers[defaultLayer] ? defaultLayers[defaultLayer] : streetView

      const mapOptions = Object.assign(this.defaultMapOptions, {
        layers: [baseLayer],
      })

      const map = L.map(this.$refs.map, mapOptions)
      this.map = map

      if (!isEmpty(this.mapProps.bounds)) {
        map.fitBounds(L.latLngBounds(this.mapProps.bounds))
      }

      new GeoSearchControl({
        provider: new OpenStreetMapProvider(),
        searchLabel: this.$i18n.t('enter_address'),
        autoClose: true,
      }).addTo(map)

      this.checkDrawAndEdit()
      this.markerCluster.addTo(map)

      if (!isEmpty(this.layerGroups)) {
        forOwn(this.layerGroups, (value, key) => {
          value.addTo(map)
        })
      }

      const availableLayers = get(this.options, 'availableViews', {})
      defaultLayers = pickBy(defaultLayers, (value, key) => !!availableLayers[key])
      const mapLayers = this.getLocalizedLayers(defaultLayers)
      L.control.layers(mapLayers, this.layerGroups || {}).addTo(map)

      this.$emit('map-changed', {
        payload: this.markerCluster.toGeoJSON(),
        onMounted: true,
      })

      this.handleMapEvents(map)
    },
  },
  watch: {
    computedReadOnly() {
      this.checkDrawAndEdit()
    },
    'field.value'() {
      // Suggests db problem..
      const data =
        this.field.value && typeof this.field.value !== 'object' ? JSON.parse(this.field.value) : this.field.value

      if (JSON.stringify(this.markerCluster.toGeoJSON()) === JSON.stringify(data)) return

      this.markerCluster.clearLayers()
      this.populateMarkerCluster(data)
    },
  },
  created() {
    if (isEmpty(this.mapProps.overlays) && this.field.value) {
      const value = isString(this.field.value) ? JSON.parse(this.field.value) : this.field.value
      this.populateMarkerCluster(value)
    }
    if (!isEmpty(this.mapProps.overlays)) {
      forOwn(this.mapProps.overlays, (value, key) => {
        const layerGroup = this.createLayerGroup(value.data)
        this.bindPropertiesToLayers(layerGroup, key)
        this.layerGroups[this.$i18n.t(key)] = L.featureGroup.subGroup(this.markerCluster, layerGroup.getLayers())
      })
    }
  },
  mounted() {
    this.coreParamsCache = cloneDeep(this.$CORE.params)

    this.setDefaultMarkerIcon()
    this.setMapProperties()
    this.setLocales()
  },
  beforeDestroy() {
    this.map.remove()
  }
}
</script>

<style lang="scss">
@import '../../../node_modules/leaflet/dist/leaflet.css';
@import '../../../node_modules/leaflet.pm/dist/leaflet.pm.css';
@import '../../../node_modules/leaflet-geosearch/assets/css/leaflet.css';
@import '../../../node_modules/leaflet.markercluster/dist/MarkerCluster.css';
@import '../../../node_modules/leaflet.markercluster/dist/MarkerCluster.Default.css';
.map {
  max-height: 60vh;
  min-height: 480px;
}
.leaflet-control-attribution {
  display: none;
}
.leaflet-top {
  z-index: 500 !important;
}
.leaflet-top.leaflet-right .leaflet-control {
  margin-top: 10px;
}
.leaflet-control-layers-list {
  padding: 0;
}
.buttonContanier {
  display: grid;
}
.leaflet-buttons-control-button.disabled .control-icon {
  opacity: 0.3;
}
.leaflet-bar-part.leaflet-bar-part-single.disabled::after {
  opacity: 0.3;
}
.leaflet-bar-part.leaflet-bar-part-single.disabled::before {
  opacity: 0.3;
}
.leaflet-pane.leaflet-shadow-pane {
  .icon-class-blue {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(180deg);
  }
  .icon-class-lilac {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(-140deg);
  }
  .icon-class-magenta {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(-100deg);
  }
  .icon-class-pink {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(-60deg);
  }
  .icon-class-orange {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(-20deg);
  }
  .icon-class-green {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(60deg);
  }
  .icon-class-cyan {
    filter: invert(0.5) sepia(1) saturate(10) hue-rotate(120deg);
  }
}
.leaflet-pane.leaflet-marker-pane {
  .icon-class-blue {
    border-radius: 50%;
    background-color: white;
  }
}
.leaflet-popup {
  .template-viewer {
    position: relative;
    width: max-content;
    max-width: 50vw;
    overflow: scroll;
  }
}
</style>
